import {
  Condition,
  ConditionItemGroup,
  Rule,
  RuleGroup,
  VuetifyShapeForSelect
} from "@/types/conditions";
import defensiveCopy from "@/util/defensive-copy";

export function removeEmptyRulesFromRuleGroup(ruleGroup: RuleGroup[]) {
  const ruleGroupHasSlugAndValue = (rule: Rule) => {
    // don't remove these operands. They won't have a value and that's fine
    if (rule.operand === "is empty" || rule.operand === "is not empty") {
      return true;
    }

    if (!rule.ruleSlug) {
      return false;
    }

    if (Array.isArray(rule.value)) {
      return rule.value.length;
    }

    return rule.value !== undefined && rule.value !== "";
  };

  return ruleGroup
    .map(ruleGroup => ruleGroup.filter(ruleGroupHasSlugAndValue))
    .filter(ruleGroup => ruleGroup.length);
}

export function getItemGroupsThatApplyToTemplateTypes(
  items: ConditionItemGroup[],
  templateTypes: string[]
): ConditionItemGroup[] {
  return items.filter(item => {
    if (templateTypes && "availableForType" in item && item.availableForType) {
      const availableForType = item.availableForType;

      return templateTypes.find(templateType =>
        availableForType.includes(templateType)
      );
    }

    return true;
  });
}

export function getVuetifyShapeForDropdownFromConditionItemGroup(
  items: ConditionItemGroup[]
) {
  const vuetifyShape: VuetifyShapeForSelect[] = [];

  items.forEach(itemGroup => {
    vuetifyShape.push({ header: itemGroup.label });
    vuetifyShape.push(...itemGroup.items);
    vuetifyShape.push({ divider: true });
  });
  vuetifyShape.pop();

  return vuetifyShape;
}

export function getConditionsThatApplyToTemplateTypes(
  availableConditions: Condition[],
  type: string[]
) {
  return (
    availableConditions
      .map(conditionArg => {
        const condition = defensiveCopy(conditionArg);

        if (condition.values) {
          condition.values = getItemGroupsThatApplyToTemplateTypes(
            condition.values,
            type
          );
        }

        return condition;
      })
      // filter out empty arrays but leave "false" and strings
      .filter(
        condition =>
          !Array.isArray(condition.values) || condition.values.length > 0
      )
  );
}

export function getConditionsThatAreAvailableForTemplateType(
  conditions: Condition[],
  type: string | undefined
) {
  return conditions.filter(({ availableForType }) => {
    const templateIsAvailableForType = type && availableForType.includes(type);

    return templateIsAvailableForType || availableForType.includes("ALL");
  });
}

// NOTE: also present in Breakdance\Themeless\Rules constants.php
export const OPERANDS = {
  IS: "is",
  IS_NOT: "is not",
  ONE_OF: "is one of",
  ALL_OF: "is all of",
  NONE_OF: "is none of",
  BEFORE: "is before",
  AFTER: "is after",
  GREATER_THAN: "is greater than",
  LESS_THAN: "is less than",
  CONTAINS: "contains",
  NOT_CONTAIN: "does not contain"
};
